import { renataChatSearch } from "@/lib/renata/experience/chat-search";
/**
 * /app/renata/handoff — Operator view for conversations waiting for human agent.
 */
import { RenataPageChrome } from "@/components/renata/RenataPageChrome";
import { createFileRoute } from "@tanstack/react-router";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { clientRequestHeaders } from "@/lib/sm-api";
import { useEffect, useState, useCallback } from "react";
import { UserCheck, Clock, Loader2, MessageSquare, RefreshCw, PhoneOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/app/renata/handoff")({
  component: RenataHandoffPage,
  head: () => ({ meta: [{ title: "Renata — Atención humana" }] }),
});

interface HandoffConversation {
  id: string;
  title: string | null;
  handoff_status: string;
  assigned_operator_id: string | null;
  handoff_at: string | null;
  updated_at: string;
}

function HandoffStatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; className: string }> = {
    waiting_human: { label: "Esperando atención", className: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300" },
    human: { label: "Atención humana", className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
    resumed_ai: { label: "Devuelta a Renata", className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" },
    ai: { label: "IA atendiendo", className: "bg-muted text-muted-foreground" },
  };
  const cfg = map[status] ?? map.ai;
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", cfg.className)}>
      {cfg.label}
    </span>
  );
}

function RenataHandoffPage() {
  const tenantId = useWorkspaceTenantId();
  const [conversations, setConversations] = useState<HandoffConversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [waitRes, humanRes] = await Promise.all([
        fetch("/api/renata/handoff?status=waiting_human", {
          credentials: "include",
          headers: clientRequestHeaders(tenantId),
        }),
        fetch("/api/renata/handoff?status=human", {
          credentials: "include",
          headers: clientRequestHeaders(tenantId),
        }),
      ]);
      const [waitData, humanData] = await Promise.all([
        waitRes.json() as Promise<{ conversations: HandoffConversation[] }>,
        humanRes.json() as Promise<{ conversations: HandoffConversation[] }>,
      ]);
      setConversations([
        ...(waitData.conversations ?? []),
        ...(humanData.conversations ?? []),
      ]);
    } catch {
      toast.error("No se pudo cargar el listado de handoff.");
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  async function handleAction(convId: string, action: "claim" | "release") {
    if (!tenantId) return;
    setActioning(convId);
    try {
      const res = await fetch(`/api/renata/handoff?action=${action}`, {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tenantId), "Content-Type": "application/json" },
        body: JSON.stringify({ conversationId: convId }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? "Error");
      }
      toast.success(action === "claim" ? "Conversación asignada." : "Conversación devuelta a Renata.");
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No pudimos actualizar la atención humana.");
    } finally {
      setActioning(null);
    }
  }

  if (!tenantId) return null;

  return (
    <div className="flex min-w-0 flex-col p-4 md:p-6 space-y-4">
      <RenataPageChrome
        screenId="handoff"
        title="Atención humana"
        subtitle="Tomá la conversación cuando quieras intervenir personalmente. Podés pausar a Renata, responder y devolvérsela."
        showBack
        breadcrumb={["Equipo", "Atención humana"]}
      >
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={() => void load()} disabled={loading}>
            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          </Button>
        </div>
      </RenataPageChrome>

      <div className="flex-1">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && conversations.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center max-w-md mx-auto">
            <UserCheck className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm font-medium">Nadie espera atención humana</p>
            <p className="text-xs text-muted-foreground">
              Cuando un cliente pida hablar con una persona, o cuando pases una conversación a tu equipo, va a aparecer acá.
            </p>
          </div>
        )}

        {!loading && conversations.length > 0 && (
          <div className="space-y-3">
            {conversations.map((conv) => (
              <div key={conv.id} className="rounded-lg border bg-card p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-sm">
                      {conv.title ?? "Conversación sin título"}
                    </p>
                    {conv.handoff_at && (
                      <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        Solicitado:{" "}
                        {new Date(conv.handoff_at).toLocaleString("es-AR", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })}
                      </p>
                    )}
                  </div>
                  <HandoffStatusBadge status={conv.handoff_status} />
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <Link
                    to="/app/renata/chat"
                    search={renataChatSearch({ c: conv.id })}
                    className="flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium hover:bg-muted"
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    Ver conversación
                  </Link>

                  {conv.handoff_status === "waiting_human" && (
                    <Button
                      size="sm"
                      className="h-8 gap-1.5 bg-violet-600 hover:bg-violet-700 text-xs"
                      onClick={() => void handleAction(conv.id, "claim")}
                      disabled={actioning === conv.id}
                    >
                      {actioning === conv.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <UserCheck className="h-3.5 w-3.5" />
                      )}
                      Tomar conversación
                    </Button>
                  )}

                  {conv.handoff_status === "human" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1.5 text-xs"
                      onClick={() => void handleAction(conv.id, "release")}
                      disabled={actioning === conv.id}
                    >
                      {actioning === conv.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <PhoneOff className="h-3.5 w-3.5" />
                      )}
                      Devolver a Renata
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
